/**
 * The flight scene's own state, and the loop that reads it once per rendered
 * frame.
 *
 * Split out of `flight-scene.ts` for the reason every other `scene-*.ts` here
 * was — the host is at aislop's 400-line ceiling — and this is the block that
 * comes out cleanly, because the two halves of it are the same thing seen twice:
 * `SceneState` is the list of what a frame may touch, and `onFrame` is the order
 * it touches them in. Neither is reachable from outside the scene: the route
 * talks to the `FlightScene` handle `createFlightScene` returns.
 *
 * `SceneState` used to be private to `flight-scene.ts`, and the siblings that
 * needed part of it declared **structural** slices instead (`BootHost`,
 * `DrawHost`, `CameraOwner`, `CockpitReportState`). That is still how a sibling
 * should ask for a *subset*, and every one of them still does. This module is
 * the exception because it is not a subset: `onFrame` reads very nearly every
 * field there is, and re-declaring the whole interface structurally to avoid
 * importing it would have been a second copy of it to keep in step.
 *
 * The loop is the AD-007a shape: wall-clock frame time goes into a fixed-step
 * accumulator (`sim-runner.ts`), whole 60 Hz ticks come out, and the rendered
 * pose is interpolated between the last two of them — so a hitching frame
 * changes how smooth the picture is, never what the physics did.
 */

import type * as THREE from "three/webgpu";
import type { PersonalPaint } from "@red-opus/formats";
import type { Pose, SimState, SkyCondition, StickAxes, StickShaping, TimeOfDay } from "@red-opus/engine";
import type { FlightAudioEngine } from "../audio/engine.ts";
import type { PropellerDiscs } from "./scene-propellers.ts";
import type { CameraMode } from "./camera-modes.ts";
import type { CameraTarget } from "./camera-target.ts";
import { applyPose, dashQuadVisible, playerAircraftKey, updateCamera, type CameraSubject } from "./scene-camera.ts";
import { eyeOffsetFor } from "./cockpit-view.ts";
import type { CombatVisuals } from "./combat-visuals.ts";
import type { ParticleEffects } from "./scene-particles.ts";
import type { Searchlights } from "./scene-searchlights.ts";
import type { Ships } from "./scene-ships.ts";
import type { GroundShadows } from "./scene-shadows.ts";
import type { CrashMarks } from "./scene-crash-marks.ts";
import type { SpriteEffects } from "./sprite-effects.ts";
import type { CameraRig } from "./flight-view.ts";
import { firingGunRangesM, flakBurstDistancesFor, nearestOtherAircraftM } from "./frame-signals.ts";
import { silentFlight, updateFrameAudio } from "./scene-audio.ts";
import type { GunMuzzles } from "./gun-muzzles.ts";
import { gunGeometryOf, reportCockpitView, type CockpitHitState } from "./scene-cockpit-report.ts";
import { drawFrame } from "./scene-draw.ts";
import type { DashCanvasFeed } from "./cockpit-dash-quad.ts";
import { describeCockpit3d, type Cockpit3dQuad } from "./scene-cockpit-3d.ts";
import { updateCockpitInterior } from "./scene-cockpit-interior.ts";
import type { MuzzleFlashState } from "./cockpit-interior.ts";
import type { CockpitInterior } from "../three/cockpit-interior.ts";
import type { KillCamWindow } from "./scene-killcam.ts";
import { sunSuppressedCombat, sunVisibleFleet, type SunSuppression } from "./sun-blind-spot.ts";
import { reportMission } from "./mission-report.ts";
import { deflectPlayerRudder } from "./rudder-deflection.ts";
import { NO_PAD_INPUT, type PadFrame } from "./gamepad-axes.ts";
import { resolveGlance, type InputSource } from "./gamepad-blend.ts";
import { glanceUnderFreeLook, NO_FREE_LOOK, type FreeLookFrame } from "./free-look.ts";
import { glanceDirectionFor, type GlanceDirection } from "./keys.ts";
import type { ParachuteVisual } from "./scene-parachute.ts";
import type { AmbientJumpers } from "./scene-ambient-jumpers.ts";
import { NO_POINTER_INPUT } from "./pointer.ts";
import type { EncounterRunner, MissionRunner } from "./sim-runner.ts";
import type { SkyDressing } from "./sky-dressing.ts";
import type { EffectsSettings, SkyTreatment } from "./effects-settings.ts";
import type { TerrainSurface } from "./scene-terrain.ts";
import type { TimeCompressionState } from "./time-compression.ts";
import { advanceTick } from "./scene-tick.ts";
import type { AutopilotState } from "./autopilot.ts";
import type { CockpitViewState, FlightSceneCallbacks } from "./flight-scene-api.ts";
import type { RendererPlan } from "../three/renderer-mode.ts";

/** HUD refresh interval — the gauges do not need to redraw at display rate. */
const HUD_INTERVAL_MS = 80;

export interface SceneState {
  readonly scene: THREE.Scene;
  readonly rig: CameraRig;
  /** **Mutable, and replaced at most once, before the first frame** — `scene-renderer-boot.ts` has the rule and why it is safe. */
  renderer: THREE.WebGPURenderer;
  /** Which backend `renderer` was built for, and whether it may fall back (`three/renderer-mode.ts`). */
  readonly rendererPlan: RendererPlan;
  readonly container: HTMLDivElement;
  readonly runner: EncounterRunner;
  /** The same object as `runner` when a mission is being flown; `null` for the instant dogfight. */
  readonly missionRunner: MissionRunner | null;
  /**
   * True only for the genuine instant dogfight — no `MissionSpec` and no
   * caller-supplied runner, i.e. exactly the `createEncounterRunner(FIRST_ENCOUNTER, …)`
   * fallback. `/duel` and `/vcr` both pass their own `options.runner` and are
   * excluded: a duel already has `DuelOutcomeOverlay`, and a replay has
   * nothing to debrief.
   */
  readonly isDogfight: boolean;
  readonly playerModelId: string;
  /** The career pilot's paint-shop choices, or `undefined` for the baked scheme. */
  readonly playerPaint: PersonalPaint | undefined;
  readonly aircraft: THREE.Group;
  /**
   * The player's own mesh, alone, inside the pose group — the part
   * `loadAircraft` empties and disposes on an ENHANCED MODELS swap. Everything
   * else that rides with the aeroplane (the dash quad below) hangs off
   * `aircraft` instead, because a swap must not take it with it.
   */
  readonly aircraftModel: THREE.Group;
  /**
   * 3D COCKPIT's dashboard quad, a child of `aircraft` so it carries the pose
   * (`scene-cockpit-3d.ts`). Built for every scene and shown by
   * `dashQuadVisible` alone: it costs one hidden node while the setting is off,
   * and building it lazily would mean building geometry on the frame a pilot
   * flips the switch.
   */
  readonly dashQuad: Cockpit3dQuad;
  /**
   * Where the panel publishes the composed cockpit frame the quad is textured
   * from. Supplied by the route that owns both; a scene with no cockpit panel
   * over it (a test, a headless run) gets an empty feed and a quad that stays
   * hidden for want of a texture.
   */
  readonly dashFeed: DashCanvasFeed;
  /**
   * 3D COCKPIT's interior treatment and its muzzle-flash term
   * (`three/cockpit-interior.ts`), driven by `updateCockpitInterior` below.
   *
   * On `SceneState` rather than inside `loadAircraft` because it must survive
   * the aeroplane being replaced: an ENHANCED MODELS flip builds a new material
   * and this is what makes the new one carry the same uniforms as the old.
   */
  readonly cockpitInterior: CockpitInterior;
  /** Where the muzzle-flash term is in its decay (`cockpit-interior.ts`'s `MuzzleFlashState`). */
  readonly muzzleFlash: MuzzleFlashState;
  /** Last-request-wins guard for `loadAircraft`, which reruns whenever ENHANCED MODELS is flipped (`scene-boot.ts`). */
  aircraftGeneration: number;
  readonly combat: CombatVisuals;
  /** Every screen-space sprite: effect bursts, the damage trail, the fire on anything alight, placed scenery sprites. */
  readonly sprites: SpriteEffects;
  readonly particles: ParticleEffects;
  /** AD-017's night beams on the flak batteries — empty and free by day (`scene-searchlights.ts`). */
  readonly searchlights: Searchlights;
  /** I12's escort sloops — empty and free unless ENHANCED SIMULATION put one to sea (`scene-ships.ts`). */
  readonly ships: Ships;
  /** B1's ground shadows — empty and free at night and at patrol height (`scene-shadows.ts`). */
  readonly shadows: GroundShadows;
  /** AD-014c's scorch marks where aeroplanes went in — empty until one does (`scene-crash-marks.ts`). */
  readonly crashMarks: CrashMarks;
  /** AD-021's canopy (`scene-parachute.ts`). */
  readonly parachute: ParachuteVisual;
  /** AD-021b's ambience jumpers (`scene-ambient-jumpers.ts`). */
  readonly jumpers: AmbientJumpers;
  readonly audio: FlightAudioEngine;
  readonly dressing: SkyDressing;
  /** The mission's briefed hour — read by the terrain's own dimming as well as the sky's. */
  readonly timeOfDay: TimeOfDay | undefined;
  /** The mission's briefed sky word — it darkens the ground as well as the dome, so the terrain needs it. */
  readonly skyWord: SkyCondition | undefined;
  /** `?treatment=<name>`'s debug pin, or `undefined` for every ordinary flight (`effects-settings.ts`). */
  readonly skyOverride: SkyTreatment | undefined;
  /** The region's ground surface and its ENHANCED TERRAIN decoration; absent until the region has loaded. */
  terrain?: TerrainSurface;
  /** AD-012's propeller discs — cosmetic, `null` until `BARON.PAL` loads (`scene-propellers.ts`). */
  propellers: PropellerDiscs | null;
  effects: EffectsSettings;
  readonly muzzles: GunMuzzles;
  cameraMode: CameraMode;
  /** Whose aeroplane the external cameras follow; `null` is the player's own (`camera-target.ts`). */
  cameraTarget: CameraTarget;
  /** The fly-by camera's unsmoothed zoom target; `state.rig.flyByZoom` chases it (`scene-flyby-zoom.ts`). */
  flyByZoomGoal: number;
  /**
   * Keeps the loaded region's terrain lines one *original* pixel wide across
   * a resize — or a pixel-ratio change (`three/view-pixel-ratio.ts`). A no-op
   * until the region loads. The width is the **view's** in CSS pixels, never the
   * drawing buffer's (`three/polygon-outlines.ts`).
   */
  setTerrainLineWidth: (viewWidthCssPx: number) => void;
  /** Last state pushed through `onCockpitView`, so the callback fires on edges only. */
  cockpitView: CockpitViewState;
  /** Accumulated bullet holes (`scene-cockpit-report.ts`). */
  cockpitHits: CockpitHitState;
  /** Fixed at scene creation from `options.timeCompressionAvailable` — see `FlightSceneOptions`. */
  readonly allowTimeCompression: boolean;
  /** Fixed at scene creation from `options.sunBlindSpot` + the mission's briefed time of day (`sun-blind-spot.ts`). */
  readonly sun: SunSuppression;
  /** Fixed at scene creation from `options.realNavigation` — REAL NAVIGATION's waypoint-cue half (`mission-hud.ts`). */
  readonly hideWaypoint: boolean;
  /** Fixed at scene creation from `options.killCamAvailable` — `/duel` is the one caller that says no (AD-015). */
  readonly killCamAvailable: boolean;
  /** AD-015's corner window: whom it watches, the boom it watches from, and whether the route has been told (`scene-killcam.ts`). */
  readonly killCam: KillCamWindow;
  timeCompression: TimeCompressionState;
  /** G44: whether the AI currently has the player's aeroplane, and on which of the original's two banners (`autopilot.ts`). */
  autopilot: AutopilotState;
  throttle: number;
  /** The three control axes as the shaping has them this frame (AD-007n change 4). */
  stick: StickAxes;
  stickShaping: StickShaping;
  /** Which device last spoke for the stick (`gamepad-blend.ts`); carried across frames like the position itself. */
  stickSource: InputSource;
  /**
   * AD-021's bail-out setting, live (`bail-settings.ts`). Off means the key does
   * nothing at all rather than refusing with a message: with the toggle off the
   * verb does not exist.
   */
  bailEnabled: boolean;
  /**
   * Seconds `H` has been held continuously. Carried here for exactly the reason
   * `throttle` and `stick` are: `advanceBailHold` is pure and the frame loop
   * owns the number across frames.
   */
  bailHeldSeconds: number;
  lastFrameMs: number;
  lastHudMs: number;
  /**
   * What the route last asked for through `setPaused` — the P key, the map
   * sheet or the PREFERENCES overlay, already collapsed into one boolean by
   * `Fly.tsx`'s `pauseWanted`. Kept here (rather than only on the runner)
   * because it is one of the two things `silentFlight` ORs together.
   */
  paused: boolean;
  missionEnded: boolean;
  /** Set once, the tick the instant dogfight's own encounter is decided — see `isDogfight`. */
  dogfightEnded: boolean;
  disposed: boolean;
  pendingInit: Promise<void>;
}

/**
 * 3D COCKPIT's probe surface, on the canvas host as `data-dash-quad`.
 *
 * A DOM attribute rather than a scene method, for the reason `CockpitPanel.tsx`
 * gives `data-cockpit-frame`: everything the dashboard shows lives inside a
 * canvas the page cannot inspect, so without something written out in text the
 * only way to check the feature is running is to diff pixels — and on a WebGPU
 * page, on this project's own hardware, there is no working way to read those
 * pixels back at all (`docs/engineering/lessons.md`). The string is
 * `describeCockpit3d`'s, and it deliberately leads with the one reading whose
 * absence let phase 1 ship an invisible dashboard: whether the quad is in the
 * scene.
 *
 * Written only when it changes, so an unchanging flight touches the DOM once.
 */
function reportDashQuad(state: SceneState): void {
  const line = describeCockpit3d(state.dashQuad.report(state.dashFeed));
  if (state.container.dataset["dashQuad"] === line) return;
  state.container.dataset["dashQuad"] = line;
}

/**
 * Where the pilot is looking this frame, from all three devices at once: the
 * glance keys, the joystick's hat, and a right-mouse free-look drag.
 *
 * The drag beats the other two while it is live, and keeps beating them until
 * the view has eased home — `free-look.ts`'s `glanceUnderFreeLook` is the rule
 * and the reasoning. Reading the free look here also *advances* its return, so
 * this is called exactly once per frame.
 */
function lookAround(
  callbacks: FlightSceneCallbacks,
  dt: number,
  pressed: ReadonlySet<string>,
  pad: PadFrame,
): { glance: GlanceDirection; freeLook: FreeLookFrame } {
  const freeLook = callbacks.freeLook?.(dt) ?? NO_FREE_LOOK;
  const snap = resolveGlance(glanceDirectionFor(pressed), pad.glance, pad.held.has("lookBack"));
  return { glance: glanceUnderFreeLook(freeLook, snap), freeLook };
}

/**
 * Where the player's guns are this frame, for the two things that need to
 * agree about it: the rounds drawn leaving them, and the light their flashes
 * cast on the interior in 3D COCKPIT.
 *
 * One function because the breech offsets are the expensive half — a screen
 * registration against the presented panel (`gun-muzzles.ts`) — and computing
 * them twice would let the drawn burst and the light it throws come apart.
 *
 * `subject` rather than the head's final orientation, for the same reason
 * `scene-camera.ts` decides the gun nodes from it: whether the interior is lit
 * at all is "is the player in a 3D cockpit", which a free-look drag does not
 * change. The flash's *direction* still moves with the aeroplane through
 * `pose`, so a look over the shoulder sees the same burst from a new angle
 * rather than seeing it switch off.
 */
function updatePlayerGunnery(
  state: SceneState,
  frame: { readonly dt: number; readonly pose: Pose; readonly simState: SimState; readonly subject: CameraSubject },
): void {
  const guns = gunGeometryOf(frame.simState);
  const breeches = state.muzzles.breechOffsets(frame.pose, guns, eyeOffsetFor(playerAircraftKey(state)));
  state.combat.setPlayerGuns(breeches, state.muzzles.barrelOffsets(frame.pose, guns));
  updateCockpitInterior(state, {
    dtS: frame.dt,
    pose: frame.pose,
    simState: frame.simState,
    inCockpit3d: dashQuadVisible(frame.subject),
    breechWorldOffsets: breeches,
    roundsPerSecond: guns.gun.roundsPerSecond,
  });
}

export function onFrame(state: SceneState, callbacks: FlightSceneCallbacks, nowMs: number): void {
  const dt = Math.min((nowMs - state.lastFrameMs) / 1000, 0.5);
  state.lastFrameMs = nowMs;
  // Defensive against autoplay-policy suspension: a no-op once the context is already
  // running, so calling it every frame costs one property read.
  state.audio.resume();

  const pressed = callbacks.pressedKeys();
  const pointer = callbacks.pointer?.() ?? NO_POINTER_INPUT;
  // One poll per rendered frame, on the same line the mouse is read — see
  // `gamepad.ts` for why per-frame and not per fixed tick.
  const pad = callbacks.gamepad?.() ?? NO_PAD_INPUT;
  const { frame, inputs } = advanceTick(state, callbacks, { dt, pressed, pointer, pad });

  const pose = state.runner.pose();
  const flakBurstDistancesM = flakBurstDistancesFor(frame.effects, pose);
  const { glance, freeLook } = lookAround(callbacks, dt, pressed, pad);
  const targets = state.runner.targets();
  // SUN BLIND SPOT (`sun-blind-spot.ts`): `sunVisible` names every aeroplane
  // the sun currently hides, once, so the render below and the HUD arrow
  // further down read the same set and never disagree.
  const sunVisible = sunVisibleFleet(pose.position, state.runner.others(), state.sun);
  applyPose(state.aircraft, pose);
  // Paint only, and deliberately before the camera reads its visibility: it
  // uploads whatever the panel last composed and touches no sim state at all,
  // which is what lets `/vcr` run with 3D COCKPIT on and stay bit-identical
  // (`scene-cockpit-3d.ts`, and `cockpit-3d-vcr.test.ts` asserts it).
  state.dashQuad.update(state.dashFeed);
  reportDashQuad(state);
  state.combat.setFleet(sunVisible.fleet, targets, dt);
  // The camera is placed *before* the combat visuals read it: the bullet quads
  // are billboarded and rolled off its world transform, so using it a frame
  // stale points the whole burst the wrong way (and at identity on frame one).
  const subject = updateCamera(state, pose, dt, glance, freeLook.aim);
  const simState = state.runner.state();
  updatePlayerGunnery(state, { dt, pose, simState, subject });
  state.combat.update(state.runner.rounds(), state.rig.camera);
  drawFrame(state, dt, pose, simState, callbacks.onKillCam);

  const combat = sunSuppressedCombat(state.runner.combat(), sunVisible.hiddenIds);
  // A replay's `simState` came from the tape's own stick, not this frame's
  // `inputs` (`playback-runner.ts` ignores `advance`'s `inputs` argument on
  // purpose) — so the cockpit's painted control surfaces and muzzle flash
  // must read whichever stick actually produced `simState`. `activeInputs`
  // is unset for every live runner, where `inputs` already is that stick.
  const cockpitInputs = state.runner.activeInputs?.() ?? inputs;
  reportCockpitView(state, callbacks, { glance, freeLook: freeLook.active, inputs: cockpitInputs, simState, gunHitsTaken: combat.gunHitsTaken });
  // The *modelled* rudder, on the same stick and for the same reason: it is a
  // surface on the player's own airframe, seen from the chase view and over his
  // shoulder, and a replay has to move it the way the tape did.
  // `flight/rudder-deflection.ts` has the rest, including why the AI fleet's
  // rudders stay centred.
  deflectPlayerRudder(state.aircraft, cockpitInputs);
  // Re-asserted every frame rather than only on the edge: `missionEnded` and
  // `dogfightEnded` are latched by `reportMission` below, which nobody calls
  // `setPaused` for. Idempotent — a gate already in the state it is asked for
  // schedules nothing.
  state.audio.setPaused(silentFlight(state));
  updateFrameAudio(state.audio, {
    dt,
    inputs,
    simState,
    burning: combat.burning,
    nearestEnemyRangeM: nearestOtherAircraftM(state.runner, pose),
    flakBurstDistancesM,
    firingGunRangesM: firingGunRangesM(state.runner, pose),
    enhancedEffects: state.effects.enhancedEffects,
  });

  if (nowMs - state.lastHudMs >= HUD_INTERVAL_MS) {
    state.lastHudMs = nowMs;
    callbacks.onHud(state.runner.hud());
    callbacks.onCombat(combat);
    reportMission(state, callbacks);
  }
}

export function startLoop(state: SceneState, callbacks: FlightSceneCallbacks): void {
  state.lastFrameMs = performance.now();
  state.renderer.setAnimationLoop(() => onFrame(state, callbacks, performance.now()));
}
